import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import ContentCard from "../components/ContentCard";
import { getContents } from "../api/contentApi";

const normalizeText = (value) =>
  String(value || "")
    .toLowerCase()
    .replace(/[ي]/g, "ی")
    .replace(/[ك]/g, "ک")
    .trim();

function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialQuery = searchParams.get("q") || "";
  const [query, setQuery] = useState(initialQuery);
  const [activeType, setActiveType] = useState(searchParams.get("type") || "all");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    setQuery(searchParams.get("q") || "");
    setActiveType(searchParams.get("type") || "all");
  }, [searchParams]);

  useEffect(() => {
    const fetchAll = async () => {
      try {
        setLoading(true);
        setError("");

        const [news, courses] = await Promise.all([
          getContents("news"),
          getContents("course"),
        ]);

        setItems([...news, ...courses]);
      } catch (err) {
        setError(err.message || "خطا در جستجو");
      } finally {
        setLoading(false);
      }
    };

    fetchAll();
  }, []);

  const filteredItems = useMemo(() => {
    const normalizedQuery = normalizeText(query);

    return items.filter((item) => {
      const matchesType = activeType === "all" || item.type === activeType;
      const searchableText = normalizeText(
        [
          item.title,
          item.summary,
          item.category,
          item.level,
          item.duration,
          Array.isArray(item.tags) ? item.tags.join(" ") : item.tags,
        ].join(" ")
      );

      return matchesType && (!normalizedQuery || searchableText.includes(normalizedQuery));
    });
  }, [items, query, activeType]);

  const updateParams = (nextQuery, nextType) => {
    const params = new URLSearchParams();

    if (nextQuery?.trim()) params.set("q", nextQuery.trim());
    if (nextType && nextType !== "all") params.set("type", nextType);

    setSearchParams(params);
  };

  return (
    <main className="content-page">
      <section className="page-hero glass-card">
        <span className="hero-badge">جستجوی سایت</span>
        <h1>جستجو در اخبار و دوره‌ها</h1>
        <p>عبارت موردنظر را وارد کنید تا در عنوان، خلاصه، دسته‌بندی و برچسب‌ها جستجو شود.</p>
      </section>

      <section className="content-section">
        <div className="content-filter-bar glass-card">
          <div className="content-filter-search">
            <span>🔍</span>
            <input
              value={query}
              onChange={(event) => {
                setQuery(event.target.value);
                updateParams(event.target.value, activeType);
              }}
              placeholder="جستجو..."
              autoFocus
            />
          </div>

          <div className="content-filter-chips compact">
            <button
              type="button"
              className={activeType === "all" ? "active" : ""}
              onClick={() => {
                setActiveType("all");
                updateParams(query, "all");
              }}
            >
              همه
            </button>

            <button
              type="button"
              className={activeType === "news" ? "active" : ""}
              onClick={() => {
                setActiveType("news");
                updateParams(query, "news");
              }}
            >
              اخبار
            </button>

            <button
              type="button"
              className={activeType === "course" ? "active" : ""}
              onClick={() => {
                setActiveType("course");
                updateParams(query, "course");
              }}
            >
              دوره‌ها
            </button>
          </div>
        </div>

        {loading && <div className="content-state glass-card">در حال جستجو...</div>}

        {!loading && error && (
          <div className="content-state content-state-error glass-card">{error}</div>
        )}

        {!loading && !error && filteredItems.length === 0 && (
          <div className="content-state glass-card">
            نتیجه‌ای پیدا نشد.
            <div className="content-state-actions">
              <Link to="/news" className="primary-link">مشاهده اخبار</Link>
              <Link to="/courses" className="primary-link">مشاهده دوره‌ها</Link>
            </div>
          </div>
        )}

        {!loading && !error && filteredItems.length > 0 && (
          <div className="content-grid">
            {filteredItems.map((item, index) => (
              <ContentCard key={item._id || item.id} item={item} priority={index < 3} />
            ))}
          </div>
        )}
      </section>
    </main>
  );
}

export default SearchPage;
